import styled, { css } from 'styled-components'

import { ReactComponent as Dots } from 'presentation/assets/icons/three-dots.svg'
import { MenuItem } from '@material-ui/core'
import { Tooltip } from '../../../shared/components/Tooltip'
import { SurgicalOrderSituation } from 'common/enum/surgical-order-situation'
import { SurgicalPendencyStatusTranslated } from 'domain/entities/surgical-pendency'
import { AuthorizationStatusTranslated as AuthorizationStatusTranslatedEnum } from 'common/enum/authorization-status'
import theme from 'presentation/styles/theme'
import { AuthorizedCommunicationStatus } from 'common/enum/authorized-communication-status'

type SituationStatus = {
  status: SurgicalOrderSituation
}

type InteractionsPendencyStatus = {
  status: SurgicalPendencyStatusTranslated
}

type InteractionsAuthorizedCommunicationStatus = {
  status: AuthorizedCommunicationStatus
}

type AuthorizationStatusColor = {
  status: AuthorizationStatusTranslatedEnum
}

type BoxDefaultProps = {
  lineClamp?: boolean
  centered?: boolean
  color?: keyof typeof theme.ds.colors
}

type BadgeColors = {
  color?: keyof typeof theme.ds.colors
  bgColor?: keyof typeof theme.ds.colors
}

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const Filters = styled.form`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 32px;

  .inputs {
    flex: 3;
    min-width: 250px;
  }

  .actions {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }
`

// TODO: Add BG Color logic
export const StatusBadge = styled.span`
  text-align: center;
  /* white-space: pre-wrap; */
  /* word-wrap: break-word; */
  white-space: nowrap;
  word-break: keep-all;
  padding: 4px 8px;
  border: 2px solid ${({ theme }) => theme.ds.colors.primary800};
  border-radius: 16px;
  line-height: 1.2;
  font-size: ${({ theme }) => theme.ds.typography.size.xsmall};
`

export const BoxDefault = styled.span<BoxDefaultProps>`
  ${({ lineClamp, centered, color, theme }) => css`
    display: block;
    width: fit-content;
    text-align: ${centered ? 'center' : 'left'};
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.3;
    ${color && `color: ${theme.ds.colors[color]};`}

    ${lineClamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
  `}
`

// export const BoxDefaultCentered = styled.span<BoxDefaultProps>`
//   ${({ lineClamp }) => css`
//     display: block;
//     width: fit-content;
//     text-align: center;
//     margin: 0 auto;
//     line-height: 1.3;
//     word-wrap: break-word;
//     white-space: pre-wrap;

//     ${lineClamp &&
//     css`
//       display: -webkit-box;
//       -webkit-line-clamp: 2;
//       -webkit-box-orient: vertical;
//       overflow: hidden;
//       text-overflow: ellipsis;
//     `}
//   `}
// `

export const BadgesWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 4px;
`

export const Badge = styled.div<BadgeColors>`
  ${({ theme, color = 'white', bgColor = 'secondary400' }) => css`
    padding: 2px 4px;
    border-radius: 8px;
    font-size: ${theme.ds.typography.size.xxsmall};
    font-weight: ${theme.ds.typography.weight.bold};
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    color: ${theme.ds.colors[color]};
    background-color: ${theme.ds.colors[bgColor]};
  `}
`

export const SituationStatusBadge = styled.span<SituationStatus>`
  ${({ theme, status }) => css`
    display: block;
    text-align: center;
    padding: 4px 8px;
    border-radius: 16px;
    font-weight: ${theme.ds.typography.weight.semiBold};
    font-size: ${theme.ds.typography.size.xsmall};
    color: ${theme.ds.colors.success900};
    background-color: ${theme.ds.colors.success100};

    ${status === SurgicalOrderSituation.ORANGE &&
    css`
      color: ${theme.ds.colors.attention900};
      background-color: ${theme.ds.colors.attention100};
    `}

    ${status === SurgicalOrderSituation.RED &&
    css`
      color: ${theme.ds.colors.danger900};
      background-color: ${theme.ds.colors.danger100};
    `}
  `}
`

export const InteractionsPendencyStatusBadge = styled.div<InteractionsPendencyStatus>`
  ${({ theme, status }) => css`
    padding: 4px 8px;
    border-radius: 16px;
    font-weight: ${theme.ds.typography.weight.semiBold};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    gap: 4px;
    width: fit-content;
    background-color: ${theme.ds.colors.terciary100};

    > .number {
      font-weight: ${theme.ds.typography.weight.semiBold};
      color: ${theme.ds.colors.terciary900};
      font-size: ${theme.ds.typography.size.xsmall};
    }

    > .icon {
      width: 16px;
      height: 16px;

      path {
        fill: ${theme.ds.colors.terciary500};
      }
    }

    ${status === SurgicalPendencyStatusTranslated.PENDENT &&
    css`
      background-color: ${theme.ds.colors.secondary100};

      > .number {
        color: ${theme.ds.colors.secondary900};
      }

      > .icon {
        path {
          fill: ${theme.ds.colors.secondary500};
        }
      }
    `}

    ${status === SurgicalPendencyStatusTranslated.RESOLVED &&
    css`
      background-color: ${theme.ds.colors.primary100};

      > .number {
        color: ${theme.ds.colors.primary900};
      }

      > .icon {
        path {
          fill: ${theme.ds.colors.primary600};
        }
      }
    `}
  `}
`

export const InteractionsAuthorizedCommunicationStatusBadge = styled.div<InteractionsAuthorizedCommunicationStatus>`
  ${({ theme, status }) => css`
    padding: 4px 8px;
    border-radius: 16px;
    font-weight: ${theme.ds.typography.weight.semiBold};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    gap: 4px;
    width: fit-content;
    background-color: ${theme.ds.colors.terciary100};

    > .icon {
      width: 18px;
      height: 18px;

      path {
        fill: ${theme.ds.colors.terciary500};
      }
    }

    ${status === AuthorizedCommunicationStatus.IN_PROGRESS &&
    css`
      background-color: ${theme.ds.colors.secondary100};

      > .icon {
        path {
          fill: ${theme.ds.colors.secondary500};
        }
      }
    `}

    ${status === AuthorizedCommunicationStatus.RESOLVED &&
    css`
      background-color: ${theme.ds.colors.primary100};

      > .icon {
        path {
          fill: ${theme.ds.colors.primary600};
        }
      }
    `}
  `}
`

export const CreatedByProfiles = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > .profile {
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    font-size: ${({ theme }) => theme.ds.typography.size.xsmall};
    color: ${({ theme }) => theme.ds.colors.white};
    font-weight: ${({ theme }) => theme.ds.typography.weight.bold};
    background-color: ${({ theme }) => theme.ds.colors.primary400};
    border: 1px solid ${({ theme }) => theme.ds.colors.neutral100};

    &:nth-child(2) {
      z-index: 1;
      margin-left: -8px;
    }

    &:hover {
      z-index: 10;
    }
  }
`

export const AuthorizationStatus = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .infos {
      display: flex;
      flex-direction: column;
      gap: 2px;
      line-height: 1.1;

      > .status,
      > .date {
        font-size: ${theme.ds.typography.size.small};
        font-weight: ${theme.ds.typography.weight.semiBold};
        color: ${theme.ds.colors.neutral700};
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      > .date {
        font-size: ${theme.ds.typography.size.xsmall};
      }
    }
  `}
`

export const CircleFill = styled.span<AuthorizationStatusColor>`
  ${({ theme, status }) => css`
    display: block;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${theme.ds.colors.neutral500};

    ${status === AuthorizationStatusTranslatedEnum.AUTHORIZED &&
    css`
      background-color: ${theme.ds.colors.success500};
    `}

    ${status === AuthorizationStatusTranslatedEnum.REQUESTED &&
    css`
      background-color: ${theme.ds.colors.terciary500};
    `}

    ${status === AuthorizationStatusTranslatedEnum.PENDENCY &&
    css`
      background-color: ${theme.ds.colors.terciary500};
    `}

    ${status === AuthorizationStatusTranslatedEnum.PARTIALLY_AUTHORIZED &&
    css`
      background-color: ${theme.ds.colors.attention500};
    `}

    ${status === AuthorizationStatusTranslatedEnum.ANALYSIS &&
    css`
      background-color: ${theme.ds.colors.secondary500};
    `}

    ${status === AuthorizationStatusTranslatedEnum.DENIED &&
    css`
      background-color: ${theme.ds.colors.danger700};
    `}

    ${status === AuthorizationStatusTranslatedEnum.FAILURE &&
    css`
      background-color: ${theme.ds.colors.danger700};
    `}
  `}
`

export const DotsIcon = styled(Dots)`
  cursor: pointer;
  border-radius: 50%;
  padding: 2px;
  display: flex;
  align-items: center;
  margin-right: 8px;
  path {
    fill: #c0c0c0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.ds.colors.white};
  }
`

export const AlertTooltip = styled(Tooltip)`
  background: #000;
`

export const StyledItem = styled(MenuItem)`
  color: ${({ theme }) => theme.colors.primary}!important;
  font-weight: ${({ theme }) => theme.font.bold}!important;
  border-radius: 8px !important;
  padding: 8px !important;
  margin-left: 8px !important;
  margin-right: 8px !important;

  span {
    margin-left: 8px;
  }

  circle {
    display: none;
  }

  svg,
  path {
    fill: ${({ theme }) => theme.colors.primary}!important;
  }

  &:hover {
    background-color: #e4f3e1 !important;
  }
`

export const PendencyContainer = styled.div<{ color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  margin: auto;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: 400;
  font-size: 18px;
`

export const TriangleAlertIconWrapper = styled.span`
  display: flex;
  align-items: center;
  svg {
    width: 20px;
    height: 20px;
    path {
      fill: ${({ theme }) => theme.ds.colors.danger500};
    }
  }
`

export const TriangleAlertIconYellowWrapper = styled.span`
  display: flex;
  align-items: center;
  svg {
    width: 20px;
    height: 20px;
    path {
      fill: ${({ theme }) => theme.ds.colors.attention700};
    }
  }
`

export const IntegratedSurgicalOrderIconWrapper = styled.span`
  display: flex;
  align-items: center;
  svg {
    width: 20px;
    height: 20px;
    path {
      fill: ${({ theme }) => theme.ds.colors.success600};
    }
  }
`

export const MenuSurgeriesWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  padding: 2px;

  &:hover {
    background-color: ${({ theme }) => theme.ds.colors.white};
  }
`

export const BudgetSwitch = styled.div`
  display: flex;
  gap: 8px;
  margin: 'auto';
  place-content: center;
  cursor: pointer;
`

export const CardContainer = styled.div`
  gap: 16px;
  padding: 8px;
  margin-bottom: 0px;

  ${({ theme }) => css`
    &:hover {
      background-color: ${theme.ds.colors.primary25};

      p {
        color: ${theme.ds.colors.primary600};
      }

      /* .order {
        font-weight: 550;
      }
      .doctor {
        font-weight: 550;
      }
      .unit {
        font-weight: 550;
      } */
    }
  `};
`

export const CardScroll = styled.div`
  &:last-child {
    padding-bottom: 35px;
  }
`

export const StyledSurgeryItem = styled(MenuItem)`
  color: ${({ theme }) => theme.colors.primary}!important;
  font-weight: ${({ theme }) => theme.font.bold}!important;
  border-radius: 8px !important;
  padding: 0px !important;
  margin-left: 8px !important;
  margin-right: 8px !important;

  span {
    margin-left: 8px;
  }

  circle {
    display: none;
  }

  svg,
  path {
    fill: ${({ theme }) => theme.colors.primary}!important;
  }

  &:hover {
    background-color: #fff !important;
  }
`
